import React, { useEffect, useState } from "react";
import TeleconsultCard from "../../components/Cards/TeleconsultCard";
import { ConsultWithParticipants } from "../../components/interface/consult";
import { getConsults } from "../../services/apiService";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import TeleconsultCardTwo from "../../components/Cards/TeleconsultCardTwo";

interface TeleconsultHistoryProps {
  patientId: string;
}

const TeleconsultHistory: React.FC<TeleconsultHistoryProps> = ({ patientId }) => {
  const [history, setHistory] = useState<ConsultWithParticipants[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0); // 0-based index
  const [rowsPerPage, setRowsPerPage] = useState<number>(9);
  const [totalPages, setTotalPages] = useState<number>(1);

  const fetchPatientHistory = async (page: number, limit: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getConsults(patientId, page + 1, limit); // API uses 1-based indexing
      const fetchedHistory = data.data.consults.map((historyItem: any) => ({
        ...historyItem,
        participants: {
          publisher: historyItem.participants?.filter((p: any) => p.role === "publisher"),
          subscriber: historyItem.participants?.filter((p: any) => p.role === "subscriber"),
        },
      }));

      setHistory(fetchedHistory);
      setTotalPages(data.data.pagination.last_page);
    } catch (error: any) {
      setError(error.message || "Failed to fetch teleconsult history.");
      console.error("Error details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientHistory(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(0);
  };
  return (
    <div className="p-4 sm:p-6 my-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
        </div>
      )}
      {error && <p className="text-red-500 font-medium text-sm">{error}</p>}
      {!loading && !error && history.length > 0 && (
        <>
          {/* Scrollable Teleconsult Cards Section */}
          <div className="scroll-container flex-grow overflow-y-auto max-h-[calc(93vh-150px)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {history.map((historyItem) => (
                <TeleconsultCardTwo
                  key={historyItem.id}
                  teleconsultHistory={historyItem}
                  participantName=""
                />
              ))}
            </div>
          </div>
  
          {/* Pagination Controls */}
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 border-t border-slate-100 pt-4">
            <div className="flex items-center text-sm text-slate-500 font-medium">
              <label htmlFor="rowsPerPage" className="text-slate-600">Rows per page:</label>
              <select
                id="rowsPerPage"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="ml-2 border border-slate-200 rounded-xl px-2 py-1 bg-slate-50 text-slate-700 outline-none hover:border-slate-300 transition-all font-semibold"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
  
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-100 hover:text-slate-800 transition-all"
              >
                <FirstPageIcon fontSize="small" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-100 hover:text-slate-800 transition-all"
              >
                <NavigateBeforeIcon fontSize="small" />
              </button>
              <span className="text-sm font-semibold text-slate-600 px-2">
                Page {currentPage + 1} of {totalPages || "?"}
              </span>
  
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-100 hover:text-slate-800 transition-all"
              >
                <NavigateNextIcon fontSize="small" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-100 hover:text-slate-800 transition-all"
              >
                <LastPageIcon fontSize="small" />
              </button>
            </div>
          </div>
        </>
      )}
      {!loading && !error && history.length === 0 && (
        <div className="text-center py-12 text-slate-400 font-medium italic">
          No teleconsult history found.
        </div>
      )}
    </div>
  );
  
};

export default TeleconsultHistory;
